import React, { useEffect, useRef, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import { closed } from "../../components/drawer/drawerSlice";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import {
  createMealType,
  getAllActiveMealTypes,
  getAllMealType,
  getMealTypeByID,
  updateMealType,
} from "./services/mealtype.services";
import { clearMealTypeDetails } from "./mealtypeSlice";
import { Typography } from "@progress/kendo-react-common";
import { RxCross1 } from "react-icons/rx";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import IconButton from "../../components/common/IconButton";

const CreateMealType: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);

  const MealTypeDetail = useAppSelector(
    (state) => state.mealType.MealTypeDetail,
  );
  const MealTypeID = useAppSelector((state) => state.drawer.data);
  const formLoading = useAppSelector((state) => state.mealType.formLoading);
  const isIndirect = useAppSelector((state) => state.drawer.isIndirect);
  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    if (MealTypeID) {
      dispatch(getMealTypeByID(MealTypeID));
    }
  }, [MealTypeID]);

  useEffect(() => {
    return () => {
      dispatch(clearMealTypeDetails());
      dispatch(closed());
    };
  }, []);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [MealTypeDetail]);

  const handleSubmit = async (values: any) => {
    try {
      if (!MealTypeID) {
        const payload = {
          MealType: values.MealType,
          Description: values.Description,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(createMealType(payload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearMealTypeDetails());
          if (isIndirect) {
            dispatch(getAllActiveMealTypes());
          } else {
            dispatch(getAllMealType());
          }
        }
      } else {
        const payload = {
          ID: MealTypeID,
          MealType: values.MealType,
          Description: values.Description,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(updateMealType(payload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearMealTypeDetails());
          dispatch(getAllMealType());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
  };

  return (
    <>
      {formLoading && <LoadingPanel gridRef={gridRef} />}
      <Form
        key={formKey}
        onSubmit={handleSubmit}
        initialValues={MealTypeDetail}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement style={{ width: "100%" }}>
            <GridLayout
              style={{
                display: "flex",
                flexWrap: "wrap",
              }}
              gap={{ rows: 10 }}
              cols={[{ width: "100%" }]}
            >
              <GridLayoutItem
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  margin: "17px 0 8px",
                }}
              >
                <Typography.h4 className="m-0">
                  {MealTypeID ? "Update Meal Type" : "Add Meal Type"}
                </Typography.h4>
                <IconButton
                  type="button"
                  fillMode={"flat"}
                  themeColor={"dark"}
                  style={{ marginRight: 4 }}
                  onClick={() => dispatch(closed())}
                  icon={RxCross1}
                />
              </GridLayoutItem>

              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  name="MealType"
                  label="Meal Type"
                  placeholder="Meal Type"
                  astrike={true}
                  component={FormTextField}
                  validator={requiredValidator}
                />
              </GridLayoutItem>
              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  name="Description"
                  label="Description"
                  placeholder="Description"
                  component={FormTextField}
                />
              </GridLayoutItem>
              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  name="IsActive"
                  label="Status"
                  astrike={true}
                  validator={requiredValidator}
                  placeholder="i.e. Active"
                  component={FormSelectionField}
                  options={STATUSARRAY?.map((status: any) => {
                    return {
                      value: status?.value,
                      label: status?.label,
                    };
                  })}
                />
              </GridLayoutItem>

              <GridLayoutItem
                colSpan={3}
                style={{
                  textAlign: "end",
                  marginTop: 20,
                  width: "100%",
                }}
              >
                <ButtonWithLoading
                  label={MealTypeID ? "Update" : "Save"}
                  type="submit"
                  disabled={!formRenderProps.allowSubmit || formLoading}
                  loading={formLoading}
                />

                <Button
                  type="button"
                  fillMode={"outline"}
                  themeColor={"primary"}
                  style={{ marginLeft: 10 }}
                  onClick={handleFormClose}
                >
                  Cancel
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </FormElement>
        )}
      />
    </>
  );
};

export default CreateMealType;
